import { useQuery, useQueryClient } from "react-query";
import { CVS, USERS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import {
  Badge,
  Button,
  Card,
  Modal,
  Pagination,
  Spinner,
  Table,
  Tabs,
  Toast,
  ToastToggle,
} from "flowbite-react";
import { useApplication } from "../../contexts/ApplicationContext";
import { useRef, useState } from "react";
import { IoMdAdd, IoMdSearch } from "react-icons/io";
import { MdBlock, MdFilterListAlt } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { FaEdit, FaRegCopy, FaTrashAlt } from "react-icons/fa";
import EmptyTable from "./EmptyTable";
import ModalDelete from "../modals/ModalDelete";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import { Tooltip } from "react-tooltip";
import ModalCancelCV from "../modals/ModalCancelCV";
import { generateCommunicateSellPdf } from "../../utils/pdf-cv.exporter";
import SelectInput from "../inputs/SelectInput";
import { useForm } from "react-hook-form";
import Input from "../inputs/Input";
import TableLoading from "../TableLoading";
import { BsSearch, BsSend } from "react-icons/bs";
import { HiX } from "react-icons/hi";
import ModalCVLogs from "../modals/ModalCVLogs";
import { PiCheckFatDuotone } from "react-icons/pi";

const CVsTable = () => {
  const queryClient = useQueryClient();
  const { userPermission, user } = useApplication();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCv, setSelectedCv] = useState(null);
  const [isOpenModalCancel, setOpenModalCancel] = useState(false);
  const [isLoadingCancel, setIsLoadingCancel] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [errorCancelCV, setErrorCancelCV] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [nameParam, setNameParam] = useState("");
  const [loadingResendId, setLoadingResendId] = useState(null);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const onPageChange = (page) => setCurrentPage(page);
  const [cvLogs, setCvLogs] = useState([]);
  const [isOpenModalLogs, setOpenModalLogs] = useState(false);
  const itemsPerPage = 20;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();

  const {
    isLoading,
    error,
    data: cvs,
  } = useQuery(
    [
      "cvs",
      currentPage,
      nameParam,
      watch("userId"),
      watch("status"),
      watch("createdAt"),
    ],
    async () => {
      const { data: items } = await axios.get(
        `${CVS.GET_ALL}?page=${currentPage}&userId=${watch(
          "userId"
        )}&status=${watch("status")}&createdAt=${watch(
          "createdAt"
        )}&search=${nameParam}&itemsPerPage=${itemsPerPage}`
      );

      setTotalPages(items.data.totalPages || 1);
      setTotalCount(items.data.totalCount || 0);
      return items.data.data;
    }
  );

  const {
    isLoading: isLoadingUsers,
    error: errorUsers,
    data: users,
  } = useQuery(["users"], async () => {
    const { data: items } = await axios.get(
      `${USERS.GET_USERS}?itemsPerPage=-1`
    );

    return items.data.data;
  });

  const handleEditCv = (cv) => {
    navigate("/cv/new", { state: { cv } });
  };

  const handleCancelCv = (cv) => {
    setErrorCancelCV(null);
    setSelectedCv(cv);
    setOpenModalCancel(true);
  };

  const handleResendCV = async (cv) => {
    try {
      setLoadingResendId(cv.id);
      const cvRecieved = await axios.get(
        `${CVS.CONSULT_RECEIPT}/${cv?.assinaturaEletronica}`
      );

      if (cvRecieved.data.comunicado.erros?.erro) {
        throw new Error(cvRecieved.data.comunicado.erros.erro);
      }

      queryClient.invalidateQueries(["cvs", currentPage]);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Erro ao reenviar comunicado de venda."
      );
      setShowErrorToast(true);

      setTimeout(() => {
        setShowErrorToast(false);
      }, 5000);
    } finally {
      setLoadingResendId(null);
    }
  };

  const cancelCV = async (option) => {
    try {
      setIsLoadingCancel(true);

      await axios.put(`${CVS.GET_CV}`, {
        documento: selectedCv?.documento,
        placa: selectedCv?.placa,
        renavam: selectedCv?.renavam,
        numeroCRV: selectedCv?.numeroCRV,
        assinaturaEletronica: selectedCv?.assinaturaEletronica,
        motivoCancelamento: option.id,
        descricaoCancelamento: option.label,
      });

      queryClient.invalidateQueries(["cvs", currentPage]);
      setOpenModalCancel(false);
    } catch (error) {
      setErrorCancelCV(
        error?.response?.data?.message ||
          "Erro ao tentar cancelar comunicado de venda!"
      );
      console.error(error);
    } finally {
      setIsLoadingCancel(false);
    }
  };

  const handleSearchName = (e) => {
    e.preventDefault();
    setNameParam(searchName);
  };

  const handleViewLogs = async (cv) => {
    try {
      const { data } = await axios.get(`${CVS.GET_CV}/${cv.id}/logs`);
      setCvLogs(data.data);
      setOpenModalLogs(true);
    } catch (error) {
      console.error("Erro ao buscar logs", error);
    }
  };

  const handleDeleteCV = async (cv) => {
    try {
      setLoadingResendId(cv.id);

      await axios.delete(`${CVS.GET_CV}/${cv.id}`);

      queryClient.invalidateQueries(["cvs", currentPage]);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Erro ao excluir comunicado de venda."
      );
      setShowErrorToast(true);

      setTimeout(() => {
        setShowErrorToast(false);
      }, 5000);
    } finally {
      setLoadingResendId(null);
    }
  };

  return (
    <>
      {user?.Company?.hasCV ? (
        <>
          <div className="flex justify-end mb-3 mt-2 gap-2">
            <Button
              className="primary ml-auto md:ml-0 mb-5 md:mb-0 fixed md:relative bottom-4 md:bottom-0 right-6 md:right-0 z-10 md:z-0 rounded-full md:rounded-md shadow-lg md:shadow-md h-14 md:h-auto w-14 md:w-auto flex md:block justify-center items-center"
              variant="contained"
              disabled={isLoading ? true : false}
              onClick={() => navigate("/cv/new")}
            >
              <IoMdAdd className="block md:hidden text-2xl" />

              <span className="hidden md:block">
                Cadastrar comunicado de venda
              </span>
            </Button>
          </div>
          <form onSubmit={handleSearchName} className="">
            <div className="mt-2 bg-white p-3 rounded-md mt-5 shadow-sm border border-gray-200 mb-4">
              <div className="font-semibold text-sm flex gap-1 items-center">
                <MdFilterListAlt className="inline-block" />
                <div>Filtros Avançados</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3  gap-2 md:gap-4">
                <div className="mt-4">
                  <Input
                    register={register}
                    label="Data de cadastro"
                    name="createdAt"
                    type="date"
                  />
                </div>
                <div className="mt-4">
                  <SelectInput
                    register={register}
                    label="Situação"
                    name="status"
                  >
                    <option value="">Todas situações</option>
                    <option value={2}>Enviado</option>
                    <option value={0}>Em progresso</option>
                    <option value={-1}>Bloqueado</option>
                  </SelectInput>
                </div>
                <div className="mt-4">
                  <SelectInput
                    register={register}
                    label="Usuário"
                    name="userId"
                  >
                    <option value="">Todos os usuários</option>
                    {users?.map((user, idx) => (
                      <option key={idx} value={user?.id}>
                        {user?.name}
                      </option>
                    ))}
                  </SelectInput>
                </div>
              </div>
            </div>
            <div className="flex gap-1 w-full pl-3 pr-2 py-2 md:py-1 rounded-md bg-gray-300 mb-3">
              <input
                type="text"
                placeholder="Busque por placa, nome do vendedor ou nome do comprador..."
                className="border-0 py-1 text-sm mt-1 focus:outline-none focus:ring-0 focus:border-gray-300 px-0 w-full bg-gray-300 placeholder:text-gray-700"
                onChange={(e) => setSearchName(e.target.value)}
                value={searchName}
              />
              <Button
                size="sm"
                className="flex items-center bg-secondary w-20"
                type="submit"
              >
                <IoMdSearch />
              </Button>
            </div>
          </form>
          {isLoading || cvs?.length > 0 ? (
            <>
              <div className="overflow-x-auto shadow-md rounded-lg mt-0 md:mt-1">
                <Table striped>
                  <Table.Head>
                    <Table.HeadCell className="text-center bg-gray-300 p-2">
                      Data cadastro
                    </Table.HeadCell>
                    {user?.companyId == 1 && (
                      <Table.HeadCell className="text-center bg-gray-300 p-2">
                        Assinatura Eletr.
                      </Table.HeadCell>
                    )}

                    <Table.HeadCell className="text-center bg-gray-300 p-2">
                      Placa
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-300 p-2">
                      Nome Comprador
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-300 p-2">
                      Nome Vendedor
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-300 p-2">
                      Situação
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-300 p-2">
                      Usuário
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-300 p-2">
                      Ações
                    </Table.HeadCell>
                  </Table.Head>

                  <Table.Body className="divide-y">
                    {!isLoading ? (
                      <>
                        {cvs?.length > 0 && (
                          <>
                            {cvs.map((cv, index) => (
                              <Table.Row
                                key={index}
                                className="bg-white dark:border-gray-700 dark:bg-gray-800"
                              >
                                <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white text-center p-2">
                                  {moment(cv.createdAt).format("DD/MM/yyyy")}
                                </Table.Cell>
                                {user?.companyId == 1 && (
                                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white text-center p-2">
                                    {cv.assinaturaEletronica ? (
                                      <span className="text-green-500 font-semibold">
                                        Sim
                                      </span>
                                    ) : (
                                      "-"
                                    )}
                                  </Table.Cell>
                                )}
                                <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white text-center p-2">
                                  {cv.placa || "-"}
                                </Table.Cell>
                                <Table.Cell
                                  className="font-medium text-gray-900 dark:text-white text-center p-2 max-w-[180px] truncate"
                                  title={cv.nomeComprador}
                                >
                                  {cv.nomeComprador || "-"}
                                </Table.Cell>

                                <Table.Cell
                                  className="font-medium text-gray-900 dark:text-white text-center p-2 max-w-[180px] truncate"
                                  title={cv.nomeVendedor}
                                >
                                  {cv.nomeVendedor || "-"}
                                </Table.Cell>
                                <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white text-center p-2">
                                  {cv.status == "-1" && (
                                    <Badge
                                      className="w-fit mx-auto"
                                      color="failure"
                                    >
                                      Cancelado
                                    </Badge>
                                  )}
                                  {cv.status == "0" && (
                                    <Badge className="w-fit mx-auto bg-gray-200 text-gray-800">
                                      <span className="flex items-center gap-1">
                                        Progresso
                                        <span className="loading-dots" />
                                      </span>
                                    </Badge>
                                  )}
                                  {cv.status == "1" && (
                                    <>
                                      <Badge
                                        className="w-fit mx-auto"
                                        color="failure"
                                      >
                                        Erro
                                      </Badge>
                                    </>
                                  )}
                                  {cv.status == "2" && (
                                    <Badge
                                      className="w-fit mx-auto"
                                      color="success"
                                    >
                                      Enviado
                                    </Badge>
                                  )}
                                </Table.Cell>
                                <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white text-center p-2">
                                  {cv?.User?.name && (
                                    <>
                                      <Badge
                                        className="w-fit mx-auto"
                                        color="gray"
                                      >
                                        {cv?.User?.name?.split(" ")[0]}
                                      </Badge>
                                    </>
                                  )}
                                </Table.Cell>
                                <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white text-center p-2">
                                  <div className="flex gap-2 justify-center items-center">
                                    <>
                                      <Tooltip
                                        id="tooltip-view-logs"
                                        content="Histórico de ações"
                                      />
                                      <BsSearch
                                        data-tooltip-id="tooltip-view-logs"
                                        fontSize="14"
                                        className="text-gray-500 cursor-pointer"
                                        onClick={() => handleViewLogs(cv)}
                                      />
                                    </>
                                    {cv.status != "2" && (
                                      <>
                                        <Tooltip
                                          id="tooltip-edit-cv"
                                          content="Editar Comunicado de venda"
                                        />
                                        <FaEdit
                                          data-tooltip-id="tooltip-edit-cv"
                                          fontSize="16"
                                          className=" cursor-pointer"
                                          onClick={() => handleEditCv(cv)}
                                        />
                                      </>
                                    )}

                                    {cv.status == "1" && (
                                      <>
                                        <Tooltip
                                          id="tooltip-consult-recepit"
                                          content="Reenviar comunicado"
                                        />
                                        {loadingResendId === cv.id ? (
                                          <Spinner size="sm" />
                                        ) : (
                                          <BsSend
                                            data-tooltip-id="tooltip-consult-recepit"
                                            fontSize="16"
                                            className="text-gray-500 cursor-pointer"
                                            onClick={() => handleResendCV(cv)}
                                          />
                                        )}
                                      </>
                                    )}

                                    {cv.status == "2" && (
                                      <>
                                        <Tooltip
                                          id="tooltip-block-customer"
                                          content="Cancelar Comunicado de venda"
                                        />
                                        <MdBlock
                                          data-tooltip-id="tooltip-block-customer"
                                          fontSize="16"
                                          className="text-red-500 cursor-pointer"
                                          onClick={() => handleCancelCv(cv)}
                                        />
                                      </>
                                    )}
                                    {(cv.status == "-1" ||
                                      cv.status == "2") && (
                                      <>
                                        <Tooltip
                                          id="tooltip-second-via"
                                          content="Emitir 2ª via"
                                        />
                                        <FaRegCopy
                                          data-tooltip-id="tooltip-second-via"
                                          fontSize="16"
                                          className="text-gray-500 cursor-pointer"
                                          onClick={() =>
                                            generateCommunicateSellPdf(cv)
                                          }
                                        />
                                      </>
                                    )}

                                    {cv.status == "0" && (
                                      <>
                                        <Tooltip
                                          id="tooltip-delete-cv"
                                          content="Excluir comunicado"
                                        />
                                        <FaTrashAlt
                                          data-tooltip-id="tooltip-delete-cv"
                                          fontSize="16"
                                          className="text-red-400 cursor-pointer"
                                          onClick={() => handleDeleteCV(cv)}
                                        />
                                      </>
                                    )}
                                  </div>
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </>
                        )}
                      </>
                    ) : (
                      <TableLoading cols={8} />
                    )}
                  </Table.Body>
                </Table>
              </div>
              {totalCount > 0 && cvs?.length > 0 && (
                <div className="mt-3 text-center text-sm text-gray-600">
                  Exibindo {(currentPage - 1) * itemsPerPage + 1}–
                  {Math.min(currentPage * itemsPerPage, totalCount)} de{" "}
                  {totalCount} registros
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex overflow-x-auto justify-center mt-3">
                  <Pagination
                    layout="pagination"
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                    previousLabel=""
                    nextLabel=""
                    showIcons
                  />
                </div>
              )}
            </>
          ) : (
            <EmptyTable icon="search_off">
              Nenhum comunicado de venda até o momento.
            </EmptyTable>
          )}

          <ModalCancelCV
            title={`Deseja cancelar este comunicado de venda? ${selectedCv?.chaveComunicacaoVenda}`}
            isOpen={isOpenModalCancel}
            setOpenModal={setOpenModalCancel}
            isLoading={isLoadingCancel}
            handleDelete={cancelCV}
            selectedCv={selectedCv}
            error={errorCancelCV}
          />
        </>
      ) : (
        <ForbiddenQueriesPermission />
      )}

      {showErrorToast && (
        <div className="fixed bottom-5 right-5 z-50">
          <Toast>
            <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-500 dark:bg-red-800 dark:text-red-200">
              <HiX className="h-5 w-5" />
            </div>
            <div className="ml-3 text-sm font-normal">{errorMessage}</div>
            <ToastToggle onDismiss={() => setShowErrorToast(false)} />
          </Toast>
        </div>
      )}

      <ModalCVLogs
        isOpen={isOpenModalLogs}
        setOpen={setOpenModalLogs}
        logs={cvLogs}
      />
    </>
  );
};

export default CVsTable;
